import type { ReactNode } from "react";

/**
 * Closed inline-markdown subset: emphasis, inline code and links. No HTML, no generic
 * markdown parser, and no dangerouslySetInnerHTML anywhere.
 */
export function renderInlineMarkdown(text: string, allowedHosts: string[]): ReactNode[] {
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return tokens.map((token, index) => {
    if (!token) return null;
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={index}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith("`") && token.endsWith("`")) {
      return <code key={index}>{token.slice(1, -1)}</code>;
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
    if (link) {
      const [, label, href] = link;
      const host = hostOf(href);
      if (host && allowedHosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))) {
        return (
          <a key={index} href={href} rel="noopener noreferrer" target="_blank">
            {label}
          </a>
        );
      }
      // Non-allowlisted destinations render as plain copyable text with the full URL visible.
      return (
        <span key={index}>
          {label} ({href})
        </span>
      );
    }
    return <span key={index}>{token}</span>;
  });
}

export function hostOf(href: string): string | null {
  try {
    const url = new URL(href);
    if (!["http:", "https:"].includes(url.protocol)) return null;
    return url.hostname.toLowerCase();
  } catch {
    return null;
  }
}
